'use client';

import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentType,
} from 'react';
import { useIsMobile } from '@/hooks';

type WaveGridModule = typeof import('./WaveGridCanvas');

const WaveGridBackground = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [mobileIntersected, setMobileIntersected] = useState(false);
  const isMobile = useIsMobile();

  // On desktop, load immediately; on mobile, wait for intersection.
  const shouldLoad = !isMobile || mobileIntersected;

  const [WaveGridCanvas, setWaveGridCanvas] = useState<ComponentType | null>(
    null,
  );

  useEffect(() => {
    const node = containerRef.current;
    // No node or desktop: nothing to observe, shouldLoad is already derived.
    if (!node || !isMobile) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setMobileIntersected(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '200px' },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile]);

  const loadCanvas = useCallback(async () => {
    if (WaveGridCanvas) {
      return;
    }
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    if (prefersReducedMotion) {
      return;
    }

    const mod: WaveGridModule = await import('./WaveGridCanvas');
    setWaveGridCanvas(() => mod.default);
  }, [WaveGridCanvas]);

  useEffect(() => {
    if (!shouldLoad || WaveGridCanvas) {
      return;
    }
    if (typeof window === 'undefined') {
      return;
    }

    let cancelled = false;
    let idleId: number | null = null;
    let timeoutId: number | null = null;

    const start = () => {
      if (cancelled) {
        return;
      }
      loadCanvas().catch(() => {});
    };

    const win = window as Window &
      typeof globalThis & {
        requestIdleCallback?: (
          cb: IdleRequestCallback,
          opts?: IdleRequestOptions,
        ) => number;
        cancelIdleCallback?: (handle: number) => void;
      };

    if (win.requestIdleCallback) {
      const idleTimeout = isMobile ? 3000 : 1500;
      idleId = win.requestIdleCallback(start, { timeout: idleTimeout });
    } else {
      const fallbackDelay = isMobile ? 2000 : 800;
      timeoutId = win.setTimeout(start, fallbackDelay);
    }

    return () => {
      cancelled = true;
      if (idleId !== null) {
        win.cancelIdleCallback?.(idleId);
      }
      if (timeoutId !== null) {
        win.clearTimeout(timeoutId);
      }
    };
  }, [WaveGridCanvas, loadCanvas, shouldLoad, isMobile]);

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex: 0 }}
    >
      {WaveGridCanvas ? <WaveGridCanvas /> : null}
    </div>
  );
};

export default memo(WaveGridBackground);
